import SearchBar from 'components/tables/list-table/filter-pieces/SearchBar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Tooltip } from 'components/ui/tooltip';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import CasesTab from 'pages/dashboard/CasesTab';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    useLazyGetDashboardMattersWithFiltersQuery,
    useLazyGetFirmWideMattersWithFiltersQuery,
} from 'store/api/matters';
import { t } from 'utils/getTranslations';
import { warnIfKeywordsTruncated } from 'utils/multiKeywordSearch';
import HomeSection, { HOME_LINK_CLASS } from './HomeSection';

type Scope = 'mine' | 'firm-wide';

/** The Cases list's built-in view that "All cases" opens from each scope. */
const ALL_CASES_VIEW: Record<Scope, string> = { mine: 'my', 'firm-wide': 'all' };

/**
 * The dashboard's My cases and Firm-wide cases tabs, as one section with a switch between them. The search box
 * sits in the heading row, so it is one for both lists and survives a switch.
 */
export default function MyCasesSection() {
    const [scope, setScope] = useState<Scope>('mine');
    const [searchText, setSearchText] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const applySearch = useDebouncedCallback((value: string) => {
        warnIfKeywordsTruncated(value);
        setAppliedSearch(value);
    }, 600);
    const clearSearch = () => {
        setSearchText('');
        // Supersedes a keystroke still waiting in the debounce, which would otherwise restore the search.
        applySearch('');
        setAppliedSearch('');
    };
    const searchPlaceholder = scope === 'mine' ? t('home.search-my-cases') : t('search');
    const search = { search: appliedSearch, onClearSearch: clearSearch };

    return (
        <Tabs
            value={scope}
            onValueChange={(value: Scope) => setScope(value)}
            className="block min-h-0 flex-1"
            data-tour="home-my-cases"
        >
            <HomeSection
                title={t('dashboard.my-cases')}
                className="pb-2.5"
                actions={
                    <>
                        <SearchBar
                            value={searchText}
                            onChange={(e) => {
                                setSearchText(e.target.value);
                                applySearch(e.target.value);
                            }}
                            placeHolder={searchPlaceholder}
                            aria-label={searchPlaceholder}
                            className="border-border-control md:[&_input]:text-meta h-8 w-[200px] rounded-md shadow-none [&_input]:text-base [&_svg]:size-3.5"
                        />
                        <TabsList>
                            <Tooltip content={t('dashboard.tooltips.my-cases')}>
                                <TabsTrigger value="mine">{t('home.mine')}</TabsTrigger>
                            </Tooltip>
                            <Tooltip content={t('dashboard.tooltips.firm-wide-cases')}>
                                <TabsTrigger value="firm-wide">{t('home.firm-wide')}</TabsTrigger>
                            </Tooltip>
                        </TabsList>
                        <Link to={`/matters?savedView=${ALL_CASES_VIEW[scope]}`} className={HOME_LINK_CLASS}>
                            {t('home.all-cases')}
                        </Link>
                    </>
                }
            >
                <TabsContent value="mine" className="min-h-0 overflow-y-auto">
                    <CasesTab query={useLazyGetDashboardMattersWithFiltersQuery} home={search} />
                </TabsContent>
                <TabsContent value="firm-wide" className="min-h-0 overflow-y-auto">
                    <CasesTab
                        query={useLazyGetFirmWideMattersWithFiltersQuery}
                        sort="caseNumber,asc"
                        emptyTitleId="dashboard.empty.firm-cases-title"
                        emptyBodyId="dashboard.empty.firm-cases-body"
                        emptySurface="dashboard-firm-cases"
                        home={search}
                    />
                </TabsContent>
            </HomeSection>
        </Tabs>
    );
}
